import type {
  StatsQuery,
  DailyStat,
  MethodStat,
  CategoryStat,
  UserStat,
  StatsSummary,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface StatsState {
  dailyData: DailyStat[];
  methodStats: MethodStat[];
  categoryStats: CategoryStat[];
  userStats: UserStat[];
  summary: StatsSummary;
  status: StateStatus;
  filter: StatsQuery;
}

interface StatsActions {
  fetchStats: (workspaceId: number, query?: StatsQuery) => Promise<void>;
  setFilter: (filter: StatsQuery) => void;
  reset: () => void;
}

export type StatsStore = StatsState & StatsActions;

const initialSummary: StatsSummary = {
  totalExpense: 0,
  totalIncome: 0,
  balance: 0,
};

const initialState: StatsState = {
  dailyData: [],
  methodStats: [],
  categoryStats: [],
  userStats: [],
  summary: initialSummary,
  status: "idle",
  filter: {},
};

export function createStatsStore(deps: StoreDeps) {
  return createStore<StatsStore>("StatsStore", (set) => ({
    ...initialState,

    fetchStats: (workspaceId, query?) =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          set((state) => { state.filter = query ?? {}; });
          const result = await deps.api.stats.getStats(workspaceId, query);
          set((state) => {
            state.dailyData = result.dailyData;
            state.methodStats = result.methodStats;
            state.categoryStats = result.categoryStats;
            state.userStats = result.userStats;
            state.summary = result.summary;
          });
        },
      ),

    setFilter: (filter) => set((state) => { state.filter = filter; }),

    reset: createReset(set as ImmerSet<StatsState>, initialState),
  }));
}
